import {
  NEW_DISCOUNT_PREFIX,
  NEW_MINIMUM_PREFIX,
  NEW_PHASE_PREFIX
} from 'Cube/domain/cube.constants'
import {
  Discount,
  Minimum,
  Phase,
  PhasePriceMetadata,
  Price,
  ResolvedPhase
} from 'Cube/domain/cube.domain.types'
import { omit } from 'lodash/fp'

export type DuplicatedPhaseData = {
  phase: Phase
  prices: Array<Price>
  discounts: Array<Discount>
  minimums: Array<Minimum>
}

export const duplicatePhase = ({
  phase
}: {
  phase: ResolvedPhase
}): {
  phase: Phase
  prices: Array<Price>
  discounts: Array<Discount>
  minimums: Array<Minimum>
} => {
  const newPrices = phase.prices.map(price => {
    return {
      ...price,
      id: price.id,
      oldPriceId: price.id
    }
  })

  const newPriceMetadata = phase.phasePriceMetadata
    .map(metadata => ({
      priceId: newPrices.find(
        ({ oldPriceId }) => oldPriceId === metadata.priceId
      )?.id,
      arrCalculation: metadata.arrCalculation ?? 'INCLUDE'
    }))
    .filter((m): m is PhasePriceMetadata => Boolean(m.priceId))

  const newDiscounts = phase.discounts.map(discount => ({
    ...discount,
    id: `${NEW_DISCOUNT_PREFIX}${crypto.randomUUID()}`,
    priceIds: discount.priceIds
      .map(
        priceId =>
          newPrices.find(newPrice => newPrice.oldPriceId === priceId)?.id
      )
      .filter((priceId): priceId is string => Boolean(priceId))
  }))

  const newMinumums = phase.minimums.map(minimum => ({
    ...minimum,
    id: `${NEW_MINIMUM_PREFIX}${crypto.randomUUID()}`,
    scope: {
      ...minimum.scope,
      priceIds: minimum.scope.priceIds
        .map(
          priceId =>
            newPrices.find(newPrice => newPrice.oldPriceId === priceId)?.id
        )
        .filter((priceId): priceId is string => Boolean(priceId))
    }
  }))

  const newPhase: Phase = {
    id: `${NEW_PHASE_PREFIX}${crypto.randomUUID()}`,
    name: '',
    priceIds: newPrices.map(({ id }) => id),
    discountIds: newDiscounts.map(({ id }) => id),
    duration:
      phase.dates.duration === 'MILESTONE'
        ? {
            years: 1,
            days: -1
          }
        : phase.dates.duration,
    minimumIds: newMinumums.map(({ id }) => id),
    recurrencePreference: phase.recurrencePreference,
    phasePriceMetadata: newPriceMetadata
  }

  return {
    phase: newPhase,
    prices: newPrices.map(omit('oldPriceId')),
    discounts: newDiscounts,
    minimums: newMinumums
  }
}
